import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import type { Program, Role } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/field';
import { Card, CardHeader, Spinner } from '@/components/ui/misc';
import { useToast } from '@/components/ui/toast';

interface Seat {
  id: string;
  title: string;
  holderName: string | null;
  userId: string | null;
  isDefaultApprover: boolean;
}
interface Member {
  userId: string;
  name: string;
  role: Role;
}

export function Seats({ program }: { program: Program }) {
  const base = `/api/programs/${program.id}/admin`;
  const qc = useQueryClient();
  const toast = useToast();
  const seats = useQuery({ queryKey: ['admin', program.id, 'seats'], queryFn: () => api.get<Seat[]>(`${base}/seats`) });
  const members = useQuery({
    queryKey: ['admin', program.id, 'members'],
    queryFn: () => api.get<{ members: Member[] }>(`${base}/members`),
  });
  const [newTitle, setNewTitle] = useState('');

  const mut = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', program.id] }),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const patch = (id: string, body: Partial<Seat>) => mut.mutate(() => api.patch(`${base}/seats/${id}`, body));

  if (seats.isLoading || members.isLoading) return <Spinner />;
  const executives = members.data!.members.filter((m) => m.role === 'EXECUTIVE' || m.role === 'ADMIN');

  return (
    <Card>
      <CardHeader
        title="Steering Committee"
        description="Seats per the Project Assignment's governance section. A seat can be named before its holder has a login. Deliverable acceptance defaults to the approver seat and can be delegated per deliverable."
      />
      <table className="w-full text-sm">
        <thead className="border-b border-border text-left text-[11px] font-medium uppercase tracking-wide text-subtle">
          <tr>
            <th className="px-5 py-2">Seat</th>
            <th className="px-3 py-2">Holder</th>
            <th className="px-3 py-2">HELM login</th>
            <th className="px-3 py-2">Default approver</th>
            <th className="w-10" />
          </tr>
        </thead>
        <tbody>
          {seats.data!.map((s) => (
            <tr key={s.id} className="border-b border-border last:border-0">
              <td className="px-5 py-2 font-medium">{s.title}</td>
              <td className="px-3 py-2">
                <Input
                  aria-label={`Holder of ${s.title}`}
                  className="h-8 max-w-56 text-[13px]"
                  placeholder="Not named yet"
                  defaultValue={s.holderName ?? ''}
                  onBlur={(e) =>
                    e.target.value !== (s.holderName ?? '') && patch(s.id, { holderName: e.target.value || null })
                  }
                />
              </td>
              <td className="px-3 py-2">
                <Select
                  aria-label={`Login for ${s.title}`}
                  className="h-8 max-w-56 text-[13px]"
                  value={s.userId ?? ''}
                  onChange={(e) => patch(s.id, { userId: e.target.value || null })}
                >
                  <option value="">Not linked</option>
                  {executives.map((m) => (
                    <option key={m.userId} value={m.userId}>
                      {m.name}
                    </option>
                  ))}
                </Select>
              </td>
              <td className="px-3 py-2">
                <input
                  type="radio"
                  name="default-approver"
                  aria-label={`${s.title} is the default approver`}
                  className="size-4 accent-[var(--brand)]"
                  checked={s.isDefaultApprover}
                  onChange={() => patch(s.id, { isDefaultApprover: true })}
                />
              </td>
              <td className="px-3 py-2">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove ${s.title}`}
                  onClick={() =>
                    confirm(`Remove the ${s.title} seat?`) && mut.mutate(() => api.delete(`${base}/seats/${s.id}`))
                  }
                >
                  <Trash2 />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <form
        className="flex gap-2 border-t border-border px-5 py-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!newTitle.trim()) return;
          mut.mutate(() => api.post(`${base}/seats`, { title: newTitle.trim() }));
          setNewTitle('');
        }}
      >
        <Input
          className="h-8 max-w-64 text-[13px]"
          placeholder="Add a seat, e.g. General Counsel"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
        />
        <Button type="submit" variant="secondary" size="sm">
          <Plus /> Add seat
        </Button>
      </form>
    </Card>
  );
}
